/**
 * "Service Audit" walkthrough.
 *
 * Explains the per-party CDD-before-service evidence register on the
 * audit-export page — what it shows, how to filter it, and the difference
 * between the CSV and Word downloads.
 *
 * Targets:
 *   data-tour="audit-header"     — page header (h2 + subtitle)
 *   data-tour="audit-downloads"  — CSV / Word download buttons
 *   data-tour="audit-filters"    — date + order-type filter toolbar
 *   data-tour="audit-summary"    — total-rows chip + truncation warning
 *   data-tour="audit-table"      — preview table
 *
 * Page is management-gated (CO/SM/GB/Admin). All steps use targetExists +
 * showOn so anything that isn't visible (e.g. when the table is empty)
 * is silently skipped rather than crashing the tour.
 */

import { NEXT, BACK, START, SKIP, DONE, targetExists } from './_helpers.js';

export const serviceAuditTour = {
    id: 'service-audit',
    title: 'Service Audit register',
    description: 'How to use the per-order CDD evidence register — filtering, downloading, and reading the table that auditors will care about.',
    autoLaunchOn: { page: 'audit-export' },

    steps: [
        {
            id: 'intro',
            title: 'Service Audit',
            text: `The Service Audit page is your <strong>per-party CDD-before-service
                evidence</strong> register. Every <em>completed</em> order on the platform
                produces one row per party (the customer plus any beneficial owners /
                associated persons), recording the CDD status of that party at the
                moment the designated service was provided.
                <br><br>
                Use this when an auditor or AUSTRAC asks for proof that customer and
                beneficial-owner identification was complete before each service was
                actually delivered.`,
            buttons: [START],
        },
        {
            id: 'downloads',
            attachTo: { element: '[data-tour="audit-downloads"]', on: 'left' },
            showOn: targetExists('[data-tour="audit-downloads"]'),
            title: 'Two download formats',
            text: `Two ways to export the same data:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>CSV</strong> — spreadsheet for finance, data work, or
                        bulk review. Includes every row regardless of preview cap.</li>
                    <li><strong>Word</strong> — landscape document, ready to open,
                        print, or hand to an auditor. Same data, formatted for
                        reading rather than analysis.</li>
                </ul>
                Both downloads honour the filters set above — narrow the view first,
                then export.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'filters',
            attachTo: { element: '[data-tour="audit-filters"]', on: 'bottom' },
            showOn: targetExists('[data-tour="audit-filters"]'),
            title: 'Filter the register',
            text: `Three filters narrow the preview and the export:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>From / To (completion date)</strong> — the window over
                        which orders were marked completed. Most audits cover a
                        financial year, so most exports start here.</li>
                    <li><strong>Order type</strong> — Trusts, SMSFs, or Companies.
                        Useful when you only need evidence for a particular product line.</li>
                </ul>
                Hit <em>Reset</em> to clear back to all-time, all-types.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'summary',
            attachTo: { element: '[data-tour="audit-summary"]', on: 'bottom' },
            showOn: targetExists('[data-tour="audit-summary"]'),
            title: 'Row count and the 500-row preview cap',
            text: `The chip shows the total number of <strong>per-party rows</strong>
                that match the current filters.
                <br><br>
                The on-screen preview is capped at <strong>500 rows</strong> for
                performance — when you exceed that, an amber notice appears reminding
                you that what you see isn't the full set. The CSV and Word downloads
                are <strong>not</strong> capped, so always export when you need the
                complete record.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'table',
            attachTo: { element: '[data-tour="audit-table"]', on: 'top' },
            showOn: targetExists('[data-tour="audit-table"]'),
            // Take the user back to the top of the page when this step
            // appears so they can see the whole layout (header + filters +
            // summary + the start of the table) in one viewport. Shepherd's
            // own scrollTo would otherwise hop straight down to the table.
            scrollTo: false,
            beforeShowPromise: () => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                // Small delay so the smooth scroll has time to start before
                // Shepherd positions the tooltip relative to the target.
                return new Promise(resolve => setTimeout(resolve, 250));
            },
            title: 'How to read each row',
            text: `Each row is one <em>party</em> on one <em>completed order</em>:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Order</strong> — the source module (Trust / SMSF /
                        Company), the order ID, the order name, and the date the
                        order was marked complete.</li>
                    <li><strong>Customer</strong> — the contracting client. The
                        badge shows their CDD status <em>at the moment the order
                        was completed</em> — it does not move when the client's
                        CDD status changes later.</li>
                    <li><strong>Designated Service</strong> — the AML/CTF service
                        category provided by this order.</li>
                    <li><strong>Party</strong> — the role chip says whether this row
                        is the customer themselves or an associated person; the AML
                        link shows whether that party has been linked to an AML
                        client record, plus the date of their last sanctions/PEP
                        screening.</li>
                </ul>`,
            buttons: [BACK, DONE],
        },
    ],
};
